<template>
  <Txt class="mt-4 flex items-center gap-2 text-base font-medium">{{ $t('Company.Customization') }}</Txt>
  <p class="text-sm text-gray-600">{{ $t('Company.CustomizationDescription')}}</p>

  <CGBanner
    :title="$t('Company.Customize')"
    :subtitle="$t('Company.CustomizeDescription')"
    variant="pink"
  >
    <template #icon>
      <font-awesome-icon :icon="['fas','palette']" class="text-pink-600 w-5 h-5" />
    </template>
  </CGBanner>

  <!-- Upload logo as implemented in CompanyPlatform -->
  <div class="space-y-6 pt-10">
    <h3 class="text-lg font-semibold flex items-center space-x-2">
      <font-awesome-icon :icon="['fas','image']" class="text-blue-800 w-5 h-5" />
      <span class="pt-1">{{ $t('Company.Logos') }}</span>
    </h3>
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">
      <FormGroup>
        <div class="space-y-4">
          <Txt class="mb-5 flex items-center gap-2 text-base font-medium">{{ $t('Company.ToggleMenuLogo') }}</Txt>
          <p class="text-sm text-gray-600">{{ $t('Company.ToggleLogoDescription')}}</p>
          <div class="flex flex-col md:flex-row gap-4 text-gray-600 cyber-card p-6 border-2 border-gray-300">
            <div class="relative h-20 w-20 flex flex-nowrap justify-center shrink-0 bg-gray-700 border border-gray-300 rounded-lg">
              <div class="absolute -top-3.5 -right-3.5">
                <CGButton :ref="draftCompany.toggleImageFilename" color="light" shape="circle" small @click="uploadLogo(draftCompany.toggleImageFilename)">
                  <Icon name="edit" :size="16" />
                </CGButton>
              </div>
              <img v-if="toggleLogoUrl" class="border border-gray-300 bg-white text-gray-300 self-center max-h-full" :alt="$t('Wcag.Preview')" aria-hidden="true" :src="toggleLogoUrl" @error="toggleLogoUrl = ''"/>
              <span v-else class="text-gray-400 text-sm sr-only">{{ $t('General.NoPreview') }}</span>
            </div>
            <div>
              <Txt as="p" small>{{ $t('Company.RecommendedImageSize') }}: (120 X 120)px</Txt>
              <Txt as="p" small>{{ $t('General.FileUploadSize', { size: maxFileSize }) }}</Txt>
              <Txt as="p" small>{{ $t('Company.RecommendedImageFormat') }}</Txt>
            </div>
          </div>
        </div>
      </FormGroup>

      <FormGroup class="!mt-0">
        <div class="space-y-4">
          <Txt class="mb-5 flex items-center gap-2 text-base font-medium">{{ $t('Company.OpenMenuLogo') }}</Txt>
          <p class="text-sm text-gray-600">{{ $t('Company.OpenMenuLogoDescription')}}</p>
          <div class="flex flex-col md:flex-row gap-4 text-gray-600 cyber-card p-6 border-2 border-gray-300">
            <div class="relative h-20 w-60 flex flex-nowrap justify-center shrink-0 bg-gray-700 border border-gray-300 rounded-lg">
              <div class="absolute flex-none -top-3.5 -right-3.5">
                <CGButton :ref="draftCompany.logoFilename" color="light" shape="circle" small @click="uploadLogo(draftCompany.logoFilename)">
                  <Icon name="edit" :size="16" />
                </CGButton>
              </div>
              <img v-if="logoUrl" class="border border-gray-300 bg-white text-gray-300 self-center max-h-full" :alt="$t('Wcag.Preview')" aria-hidden="true" :src="logoUrl" @error="logoUrl = ''"/>
              <span v-else class="text-gray-400 text-sm sr-only">{{ $t('General.NoPreview') }}</span>
            </div>
            <div>
              <Txt as="p" small class="">{{ $t('Company.RecommendedImageSize') }}: (540 X 120)px</Txt>
              <Txt as="p" small class="">{{ $t('General.FileUploadSize', { size: maxFileSize }) }}</Txt>
              <Txt as="p" small class="">{{ $t('Company.RecommendedImageFormat') }}</Txt>
            </div>
          </div>
        </div>
      </FormGroup>
    </div>
  </div>

  <div class="space-y-6 pt-10">
    <h3 class="text-lg font-semibold flex items-center space-x-2">
      <font-awesome-icon :icon="['fas','palette']" class="text-blue-800 w-5 h-5" />
      <span class="pt-1">{{ $t('Company.ColorScheme')}}</span>
    </h3>
    <div class="grid grid-cols-1 lg:grid-cols-12 gap-8">
      <div class="lg:col-span-4 space-y-6">

        <div class="cyber-card p-6 space-y-4 gap-y-2">
          <label class="flex items-center text-base font-medium" for="primaryColorPicker">{{ $t('Company.PrimaryColor')}}</label>
          <p class="text-sm text-gray-600">{{ $t('Company.PrimaryColorDescription') }}</p>
          <ColorPicker
            id="primaryColorPicker"
            v-model="draftCompany.primaryColor"
            :clearable="false"
            :hidePalette="true"
            @change="validateColor($event, 'primaryColor')"
          />
          <div class="p-3 rounded-lg" :style="`background-color: ${draftCompany.primaryColor};`">
            <span class="text-white text-sm font-medium">{{ $t('Company.Sidebar')}}</span>
          </div>
        </div>

        <div class="cyber-card p-6 space-y-4 gap-y-2">
          <label class="flex items-center text-base font-medium" for="secondaryColorPicker">{{ $t('Company.SecondaryColor')}}</label>
          <p class="text-sm text-gray-600">{{ $t('Company.SecondaryColorDescription') }}</p>
          <ColorPicker
            id="secondaryColorPicker"
            v-model="draftCompany.secondaryColor"
            :clearable="false"
            :hidePalette="true"
            @change="validateColor($event, 'secondaryColor')"
          />
          <div class="p-3 rounded-lg" :style="`background-color: ${draftCompany.secondaryColor};`">
            <span class="text-white text-sm font-medium">{{ $t('Company.InformationalCards')}}</span>
          </div>
        </div>
      </div>

      <!-- Preview Dashboard -->
      <div class="lg:col-span-8 space-y-4">
        <div class="flex items-center space-x-2 mb-4">
          <Icon name="eye" size="24" class="text-green-600" />
          <h4 class="text-base font-semibold pt-1">{{ $t('Company.PreviewDashboard') }}</h4>
        </div>
        <p class="text-sm mb-4">{{ $t('Company.PreviewDashboardDescription') }}</p>
        <div class="relative w-full rounded-lg">
          <div class="bg-gray-100 p-4 rounded-lg border-2 border-gray-200">
            <div class="bg-white rounded-lg shadow-sm overflow-hidden" style="aspect-ratio: 16 / 9;">
              <div class="flex h-full">
                <div class="w-20 flex-shrink-0 flex flex-col items-center py-4 space-y-4" :style="`background-color: ${draftCompany.primaryColor};`">
                  <div class="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                    <div class="w-6 h-6 bg-white rounded-sm"></div>
                  </div>
                  <div class="space-y-3">
                    <div v-for="i in 5" :key="i" class="w-10 h-10 bg-white/10 rounded-lg hover:bg-white/20 transition-colors cursor-pointer flex items-center justify-center">
                      <div class="w-5 h-5 bg-white/60 rounded-sm"></div>
                    </div>
                  </div>
                </div>
                <div class="flex-1 p-4 space-y-3">
                  <div class="flex items-center justify-between">
                    <h3 class="text-sm font-semibold text-gray-800">{{ $t('Dashboard.Hello', { user: userName })}}</h3>
                    <div class="flex items-center space-x-2">
                      <div class="w-6 h-6 bg-gray-300 rounded-full"></div>
                      <span class="text-xs font-medium bg-gray-100 px-2 py-1 rounded">{{ userInitials }}</span>
                    </div>
                  </div>
                  <p class="text-xs text-gray-600">{{ $t('Dashboard.HelloDescription', { displayname: draftCompany?.displayname || 'Cyber Guru' })}}</p>
                  <div class="w-48">
                    <div class="bg-card text-card-foreground flex flex-col gap-6 rounded-xl p-3 border-0 shadow-sm" :style="`background-color: ${draftCompany.secondaryColor};`">
                      <div class="flex items-center space-x-3">
                        <div class="w-8 h-8 bg-white/30 rounded-full flex items-center justify-center">
                          <div class="w-4 h-4 bg-white rounded-full"></div>
                        </div>
                        <div>
                          <div class="text-lg font-bold text-white">56</div>
                          <div class="text-xs text-white/80">{{ $t('Company.Online') }}</div>
                        </div>
                        <div class="ml-auto">
                          <div class="text-xs text-white/70">/ 716.658</div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="space-y-2">
                    <h4 class="text-xs font-semibold text-gray-700">{{ $t('Dashboard.Overview') }}</h4>
                    <div class="grid grid-cols-7 gap-1">
                      <div v-for="i in 30" :key="i" class="h-6 rounded-sm text-xs flex items-center justify-center bg-gray-100 text-gray-600">{{ i }}</div>
                    </div>
                  </div>
                  <div class="grid grid-cols-2 gap-3 mt-4">
                    <div v-for="i in 2" :key="i" class="h-8 bg-gray-100 rounded"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="text-xs bg-blue-50 p-3 rounded-lg">
          <strong>{{ $t('Company.Legend') }}</strong> {{ $t('Company.LegendDescription') }}
        </div>
      </div>
    </div>
  </div>

  <FileUploadModal ref="fileUploadModal" :trigger="fileUploadRef" :file-label="fileUploadLabel" :max-size="maxFileSize" :accepted-formats="['.JPG','.JPEG','.PNG']" @submit="submitFileUpload" />

</template>

<script>
import { defineComponent } from 'vue';

export default defineComponent({
  props: {
    company: {
      type: Object,
      required: true
    }
  },
  data: function() {
    return {
      draftCompany: { ...this.company },
      maxFileSize: "1 MB",
      fileUploadLabel: '',
      fileUploadRef: null
    };
  },
  watch: {
    company: {
      handler(newVal) {
        this.draftCompany = { ...newVal };
      },
      deep: true
    }
  },
  computed: {
    userName() {
      if (!this.$profile) return '';
      return (this.$profile.firstname && this.$profile.lastname)? (this.$profile.firstname) : this.$profile.username;
    },
    userInitials() {
      const profile = this.$user.isDemoAccount && this.$user.impersonator || this.$profile;
      if (!profile) return '';

      return (profile.firstname && profile.lastname)?
        (profile.firstname.substr(0,1) + profile.lastname.substr(0,1)).toUpperCase() :
        (profile.username.substr(0,2).toUpperCase());
    },
    logoUrl() {
      return this.draftCompany.logo ? URL.createObjectURL(this.draftCompany.logo) : '';
    },
    toggleLogoUrl() {
      return this.draftCompany.toggleImage ? URL.createObjectURL(this.draftCompany.toggleImage) : '';
    }
  },
  methods: {
    validateColor(event) {
      const colorValue = event.target.value;
      if (!/^#[a-fA-F0-9]{6}$/.test(colorValue)) { 
        this.$eventBus.$emit('show-alert', { title: this.$t('Company.RecommendedColorFormat'), variant: 'danger' })
        return;
      }
    },
    uploadLogo(fileName) {
      this.fileUploadRef = this.$refs[fileName];
      this.fileUploadLabel = fileName;
      this.$refs.fileUploadModal.showUploadModal(true);
    },
    submitFileUpload(files) {
      if (files.fileLabel == this.draftCompany.toggleImageFilename) {
        this.draftCompany.toggleImage = files.fileList[0];
      }

      if (files.fileLabel == this.draftCompany.logoFilename) {
        this.draftCompany.logo = files.fileList[0];
      }
    }
  }
});
</script>